export interface TimeZoneOption {
  value: string;
  city: string;
  offset: string;
  search: string;
}

const cityNames: Record<string, string> = {
  'America/Sao_Paulo': 'São Paulo',
  'America/Fortaleza': 'Fortaleza',
  'America/Manaus': 'Manaus',
  'America/Belem': 'Belém',
  'America/Recife': 'Recife',
  'America/Bahia': 'Salvador',
  'America/Cuiaba': 'Cuiabá',
  'America/Rio_Branco': 'Rio Branco',
  'America/Noronha': 'Fernando de Noronha',
  UTC: 'Tempo universal',
};

export function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function resolveTimeZone(
  userTimeZone: string | null | undefined,
  tenantTimeZone: string | null | undefined,
  browserTimeZone = getBrowserTimeZone(),
): string {
  return userTimeZone || tenantTimeZone || browserTimeZone || 'UTC';
}

/// O fuso em que uma linha de fato se lê (ADR 0044). Na visão consolidada cada linha fica no fuso
/// retratado da unidade de origem — somar unidades não inventa um dia comum; dentro de uma unidade
/// vale o fuso de exibição da pessoa.
export function factTimeZone(
  fact: { timeZone?: string | null | undefined },
  timeZone: string,
  consolidated: boolean,
): string {
  return consolidated && fact.timeZone ? fact.timeZone : timeZone;
}

export function formatDateTime(value: Date | string, timeZone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'medium',
    timeZone: validTimeZoneOrUtc(timeZone),
  }).format(typeof value === 'string' ? new Date(value) : value);
}

export function formatDate(value: Date | string, timeZone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeZone: validTimeZoneOrUtc(timeZone),
  }).format(typeof value === 'string' ? new Date(value) : value);
}

/// Hora e minuto, sem segundos: "10:18". É o que se lê num horário marcado ou num "salvo às".
/// O dia civil `aaaa-mm-dd` de um instante no fuso da empresa: para contar dias, não para mostrar.
export function civilDateOf(value: Date | string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: validTimeZoneOrUtc(timeZone),
  }).format(typeof value === 'string' ? new Date(value) : value);
}

/// Dias de calendário entre dois instantes, no fuso da empresa.
export function daysBetween(from: Date | string, until: Date | string, timeZone: string): number {
  return Math.round(
    (Date.parse(`${civilDateOf(until, timeZone)}T00:00:00Z`) -
      Date.parse(`${civilDateOf(from, timeZone)}T00:00:00Z`)) /
      86_400_000,
  );
}

export function formatHourMinute(value: Date | string, timeZone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: validTimeZoneOrUtc(timeZone),
  }).format(typeof value === 'string' ? new Date(value) : value);
}

export function formatTime(value: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: validTimeZoneOrUtc(timeZone),
  }).format(value);
}

function validTimeZoneOrUtc(timeZone: string): string {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0);
    return timeZone;
  } catch {
    return 'UTC';
  }
}

/// Montar a lista completa custa uma formatação de data por fuso IANA (mais de 400). O resultado só
/// muda quando o deslocamento muda, então o caso padrão é calculado uma vez por sessão em vez de a
/// cada componente montado.
let defaultOptions: TimeZoneOption[] | undefined;

export function getTimeZoneOptions(): TimeZoneOption[] {
  defaultOptions ??= createTimeZoneOptions();
  return defaultOptions;
}

export function createTimeZoneOptions(
  timeZones: string[] = Intl.supportedValuesOf('timeZone'),
  at = new Date(),
): TimeZoneOption[] {
  return [...new Set(['UTC', ...timeZones])]
    .map((value) => {
      const city = cityNames[value] ?? value.split('/').at(-1)!.replaceAll('_', ' ');
      const offset = getOffset(value, at);
      return {
        value,
        city,
        offset,
        search: normalize(`${city} ${value} ${offset}`),
      };
    })
    .sort((left, right) => left.city.localeCompare(right.city, 'pt-BR'));
}

export function filterTimeZones(options: TimeZoneOption[], search: string): TimeZoneOption[] {
  const normalized = normalize(search.trim());
  return normalized ? options.filter((option) => option.search.includes(normalized)) : options;
}

function getOffset(timeZone: string, at: Date): string {
  const value = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  })
    .formatToParts(at)
    .find((part) => part.type === 'timeZoneName')?.value;
  const match = /^GMT(?:(?<sign>[+-])(?<hour>\d{1,2})(?::(?<minute>\d{2}))?)?$/.exec(
    value ?? 'GMT',
  );
  if (!match?.groups?.sign) return 'UTC+00:00';
  const sign = match.groups.sign === '-' ? '−' : '+';
  const hour = match.groups.hour!.padStart(2, '0');
  const minute = (match.groups.minute ?? '00').padStart(2, '0');
  return `UTC${sign}${hour}:${minute}`;
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');
}
